import { SetOptional } from '@appTypes/general';
import { RuleDefinitionsReadmodelType, RulesReadmodelType } from '@bett3r-dev/teselly-domain';
// Values come from the jsonschema-free subpath, never the barrel.
import { normalizeOverrideGroup } from '@bett3r-dev/teselly-domain/automation/constants';
import { friendlyName } from '@bett3r-dev/teselly-client-library';
import RuleNodesViewModal from '@components/application/Rules/RuleNodesView/RuleNodesViewModal';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import { ActionIcon, Alert, Badge, Button, Card, Group, List, MultiSelect, NumberInput, Paper, Popover, Select, Stack, Switch, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconBug, IconEye, IconPlus, IconX } from '@tabler/icons-react';
import type { ExecutionTrace, MaterializedVariable } from '@bett3r-dev/teselly-rule-editor';
import { flattenVariablesRecord, formulaItemRefs, missingItemRefs } from '@bett3r-dev/teselly-rule-editor';
import { performRuleDryRun, RuleDryRunError, RuleDryRunStructuredError } from '@utils/rules';
import { forwardRef, KeyboardEvent, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntitySelector } from './EntitySelector';
import { FormulaInput } from './FormulaInput';
import classes from './RuleConfigurationForm.module.css';
import { TemplateInput } from './TemplateInput';
import { EntityOption, PreviewEntityProvider } from './types';
import { componentsTestIds } from '@components/testids';

interface RuleConfigurationFormProps {
  hierarchyLevel: 'channel' | 'publication' | 'publicationVariation' | 'account' | 'warehouse' | 'item';
  hierarchyEntityId?: string;
  instantiatedRule: SetOptional<RulesReadmodelType, 'hierarchyEntityIds'> | null;
  onSave: ( rule: SetOptional<RulesReadmodelType, 'hierarchyEntityIds'> ) => void;
  onCancel: () => void;
  initialData?: SetOptional<RulesReadmodelType, 'hierarchyEntityIds'>;
  isEditing?: boolean;
  selectedDefinitionId?: string | null;
  onValidationChange?: ( isValid: boolean ) => void;
  ruleDefinition?: any;
  ruleDefinitions: RuleDefinitionsReadmodelType[];
  previewEntityProvider?: PreviewEntityProvider;
}

export interface RuleConfigurationFormRef {
  save: () => void;
  isValid: () => boolean;
}

/**
 * The rule's saved input values (`declaredInputsValues`) come from the readmodel
 * as a parsed object, but defensively handle a JSON string too.
 */
const parseDeclaredInputsValues = ( raw: unknown ): Record<string, any> => {
  if ( !raw ) return {};
  if ( typeof raw === 'string' ) {
    try {
      const parsed = JSON.parse( raw );
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  if ( typeof raw === 'object' ) return raw as Record<string, any>;
  return {};
};

/** Build the per-input default values from a rule's declared inputs. */
const buildDefaultInputs = (
  rule: { declaredInputs?: { inputName?: string; defaultValue?: any; type?: string }[] } | null
): Record<string, any> => {
  const defaultInputs: Record<string, any> = {};
  rule?.declaredInputs?.forEach( declaredInput => {
    if ( declaredInput.inputName && declaredInput.defaultValue !== undefined ) {
      defaultInputs[declaredInput.inputName] = declaredInput.defaultValue;
    } else if ( declaredInput.inputName ) {
      switch ( declaredInput.type ) {
        case 'boolean':
          defaultInputs[declaredInput.inputName] = false;
          break;
        case 'number':
          defaultInputs[declaredInput.inputName] = 0;
          break;
        default:
          defaultInputs[declaredInput.inputName] = '';
      }
    }
  });
  return defaultInputs;
};

export const RuleConfigurationForm = forwardRef<RuleConfigurationFormRef, RuleConfigurationFormProps>(({ hierarchyLevel, hierarchyEntityId, instantiatedRule, onSave, onCancel, initialData, isEditing = false, selectedDefinitionId, ruleDefinition, ruleDefinitions, onValidationChange, previewEntityProvider }, ref ) => {
  const { t, i18n } = useTranslation( 'rules' );
  const { t: tAutomation } = useTranslation( 'automation' );
  const { backend } = useBackend() as BackendContextType;
  const [ selectedEntityOption, setSelectedEntityOption ] = useState<EntityOption | null>( null );
  const [ inputs, setInputs ] = useState<Record<string, any>>(() => {
    if ( initialData ) {
      return initialData.declaredInputsValues || {};
    }

    if ( !instantiatedRule ) return {};

    // Seed defaults, then overlay the rule's SAVED values (editing). Without the
    // overlay an existing rule opens showing the definition defaults instead of
    // its configured values.
    return {
      ...buildDefaultInputs( instantiatedRule ),
      ...parseDeclaredInputsValues(( instantiatedRule as any ).declaredInputsValues )
    };
  });

  const [ enabled, setEnabled ] = useState<boolean>( initialData?.enabled ?? true );
  const [ customTags, setCustomTags ] = useState<string[]>( initialData?.customTags ?? []);
  const [ customTagsInput, setCustomTagsInput ] = useState<string>( '' );
  const [ overrideGroups, setOverrideGroups ] = useState<string[]>( initialData?.overrideGroups ?? []);
  const [ overrideGroupsInput, setOverrideGroupsInput ] = useState<string>( '' );
  // An existing rule whose stored row has NO overrideGroups key. Saving it with the field still empty
  // omits the key instead of sending `[]`: the readmodel upsert replaces arrays, so an explicit `[]`
  // would clear a group the backfill later unions in (TV1-2455 D9/D13, ADR-203).
  const storedOverrideGroupsMissing = useRef<boolean>( !!initialData && initialData.overrideGroups === undefined );
  // Options are the groups the available definitions declare, one per normalised id (the first
  // declaration's spelling wins), labelled in the current language or else by id.
  const overrideGroupsOptions = useMemo(() => {
    const language = i18n.resolvedLanguage || 'es';
    const byIdentity = new Map<string, { id: string; label: string }>();
    ruleDefinitions.forEach( definition => {
      definition.definitionOverrideGroups?.forEach( group => {
        const identity = normalizeOverrideGroup( group.id );
        if ( !identity || byIdentity.has( identity )) return;
        const label = ( group.name as Record<string, string | undefined> | undefined )?.[language];
        byIdentity.set( identity, { id: group.id.trim(), label: label || group.id.trim() });
      });
    });
    return [...byIdentity.values()];
  }, [ ruleDefinitions, i18n.resolvedLanguage ]);
  const [ showOverrideGroupsDropdown, setShowOverrideGroupsDropdown ] = useState<boolean>( false );
  const [ triggeringEvents, setTriggeringEvents ] = useState<string[]>( initialData?.triggeringEvents ?? []);
  const [ showNodesEditor, setShowNodesEditor ] = useState<boolean>( false );

  const [ previewFormulaValue, setPreviewFormulaValue ] = useState<{
    success: boolean;
    value: any;
    error: string | null;
  } | null>( null );
  const [ previewFormulaValueLoading, setPreviewFormulaValueLoading ] = useState<boolean>( false );
  const [ formulaValidationError, setFormulaValidationError ] = useState<string | null>( null );
  const [ dryRunTrace, setDryRunTrace ] = useState<ExecutionTrace | null>( null );
  const [ dryRunError, setDryRunError ] = useState<RuleDryRunStructuredError | null>( null );
  const [ showDebugModal, setShowDebugModal ] = useState<boolean>( false );

  const handlePreviewFormulaValue = useCallback( async () => {
    setPreviewFormulaValueLoading( true );
    setPreviewFormulaValue( null ); // Clear previous result

    try {
      // Derive hierarchy from the selected rule definition (never a prop).
      const selectedDefinition = ruleDefinitions.find( d => d.id === selectedDefinitionId );
      const hierarchy = selectedDefinition?.hierarchy;
      if ( !hierarchy ) {
        console.error( 'RuleConfigurationForm - Missing hierarchy on selected rule definition', { selectedDefinitionId });
        setPreviewFormulaValue({
          success: false,
          value: null,
          error: t( 'preview_result_missing_hierarchy', 'No se pudo determinar la jerarquía de la regla seleccionada' )
        });
        setDryRunTrace( null );
        setDryRunError( null );
        return;
      }

      // Derive fieldType from rule categories
      const categories = instantiatedRule?.categories || [];
      const fieldType = categories.includes( 'publicationPrice' ) ? 'price' as const
        : categories.includes( 'publicationStock' ) ? 'stock' as const
        : undefined;


      const { result, trace, error: structuredError } = await performRuleDryRun({
        ruleDefinitionId: selectedDefinitionId || '',
        backend,
        declaredInputsValues: inputs,
        hierarchy,
        targetEntityData: selectedEntityOption?.data,
        targetEntityId: selectedEntityOption?.id ?? hierarchyEntityId,
        fieldType
      });

      setDryRunTrace(( trace as ExecutionTrace ) ?? null );
      setDryRunError( structuredError ?? null );

      if ( structuredError ) {
        setPreviewFormulaValue({
          success: false,
          value: null,
          error: structuredError.message
        });
      } else if ( result ) {
        setPreviewFormulaValue({
          success: true,
          value: result,
          error: null
        });
      } else {
        setPreviewFormulaValue({
          success: false,
          value: null,
          error: t( 'preview_result_no_result', 'No se obtuvo ningún resultado' )
        });
      }
    } catch ( error ) {
      console.error( 'Error previewing formula:', error );
      if ( error instanceof RuleDryRunError ) {
        setDryRunTrace(( error.trace as ExecutionTrace ) ?? null );
        setDryRunError( error.structuredError );
      } else {
        setDryRunTrace( null );
        setDryRunError( null );
      }
      setPreviewFormulaValue({
        success: false,
        value: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setPreviewFormulaValueLoading( false );
    }
  }, [ backend, selectedDefinitionId, ruleDefinitions, hierarchyEntityId, inputs, selectedEntityOption, instantiatedRule?.categories, t ]);

  /**
   * A NEW rule starts with its definition's declared group ids (TV1-2455, ADR-203). Read from the
   * selected definition in `ruleDefinitions`, falling back to the draft itself (`rules_draft`
   * spreads the definition into it). Only the new-rule resets call this; an existing rule keeps
   * its stored array, `[]` included.
   */
  const definitionGroupIds = (): string[] => {
    const definitionId = selectedDefinitionId || instantiatedRule?.ruleDefinitionId;
    const declared = ruleDefinitions.find( d => d.id === definitionId )?.definitionOverrideGroups
      ?? instantiatedRule?.definitionOverrideGroups
      ?? [];
    const seen = new Set<string>();
    return declared.map( group => group.id.trim()).filter( id => {
      const identity = normalizeOverrideGroup( id );
      if ( !identity || seen.has( identity )) return false;
      seen.add( identity );
      return true;
    });
  };

  useEffect(() => {
    if ( !isEditing && instantiatedRule && selectedDefinitionId ) {
      const defaultInputs: Record<string, any> = {};
      instantiatedRule.declaredInputs?.forEach( declaredInput => {
        if ( declaredInput.inputName && declaredInput.defaultValue !== undefined ) {
          defaultInputs[declaredInput.inputName] = declaredInput.defaultValue;
        } else if ( declaredInput.inputName ) {
          switch ( declaredInput.type ) {
            case 'boolean':
              defaultInputs[declaredInput.inputName] = false;
              break;
            case 'number':
              defaultInputs[declaredInput.inputName] = 0;
              break;
            default:
              defaultInputs[declaredInput.inputName] = '';
          }
        }
      });
      setInputs( defaultInputs );
      setEnabled( true );
      setCustomTags([]);
      setOverrideGroups( definitionGroupIds());
      setTriggeringEvents(( instantiatedRule as any ).possibleTriggeringEvents || []);
      setCustomTagsInput( '' );
      setOverrideGroupsInput( '' );
    }
  }, [ selectedDefinitionId, instantiatedRule, isEditing ]);

  // Update state when initialData changes
  useEffect(() => {
    if ( initialData ) {
      setEnabled( initialData.enabled ?? true );
      setCustomTags( initialData.customTags ?? []);
      storedOverrideGroupsMissing.current = initialData.overrideGroups === undefined;
      setOverrideGroups( initialData.overrideGroups ?? []);
      setTriggeringEvents( initialData.triggeringEvents ?? []);
      setInputs( initialData.declaredInputsValues || {});
      setShowOverrideGroupsDropdown( false );
    } else if ( isEditing && instantiatedRule ) {
      // Editing an existing rule (no initialData): restore ALL saved values from
      // the rule, overlaying saved input values on top of the definition defaults.
      const ruleAny = instantiatedRule as any;
      setEnabled( ruleAny.enabled ?? true );
      setCustomTags( ruleAny.customTags ?? []);
      storedOverrideGroupsMissing.current = ruleAny.overrideGroups === undefined;
      setOverrideGroups( ruleAny.overrideGroups ?? []);
      setTriggeringEvents( ruleAny.triggeringEvents ?? []);
      setCustomTagsInput( '' );
      setOverrideGroupsInput( '' );
      setShowOverrideGroupsDropdown( false );
      setInputs({
        ...buildDefaultInputs( instantiatedRule ),
        ...parseDeclaredInputsValues( ruleAny.declaredInputsValues )
      });
    } else {
      setEnabled( true );
      setCustomTags([]);
      storedOverrideGroupsMissing.current = false;
      setOverrideGroups( instantiatedRule ? definitionGroupIds() : []);
      setCustomTagsInput( '' );
      setOverrideGroupsInput( '' );
      setShowOverrideGroupsDropdown( false );

      if ( instantiatedRule ) {
        setInputs( buildDefaultInputs( instantiatedRule ));
      }
    }
  }, [ initialData, instantiatedRule, isEditing ]);

  // Sync enabled state
  useEffect(() => {
    if ( initialData ) {
      setEnabled( initialData.enabled ?? true );
    } else {
      setEnabled( true );
    }
  }, [initialData]);

  // Track validation
  useEffect(() => {
    if ( onValidationChange && instantiatedRule ) {
      onValidationChange( isValid());
    }
  }, [ inputs, enabled, customTags, overrideGroups, triggeringEvents, customTagsInput, overrideGroupsInput, showOverrideGroupsDropdown, instantiatedRule, onValidationChange ]);

  // Expose methods
  useImperativeHandle( ref, () => ({
    save: handleSave,
    isValid
  }), [ inputs, enabled, customTags, overrideGroups, triggeringEvents, customTagsInput, overrideGroupsInput, showOverrideGroupsDropdown, instantiatedRule ]);

  if ( !instantiatedRule ) {
    return (
      <Card withBorder radius="xs" p="md">
        <Text size="sm" c="dimmed">
          {t( 'select_rule_definition_message', 'Selecciona una definición de regla para configurar' )}
        </Text>
      </Card>
    );
  }

  const handleInputChange = ( inputName: string, value: any ) => {
    let processedValue = value;
    if ( typeof value === 'string' && (( value.startsWith( '{' ) && !value.startsWith( '{{' )) || value.startsWith( '"' ))) {
      try {
        processedValue = JSON.parse( value );
      } catch ( e ) {
        processedValue = value;
      }
    }

    // fpor multiselect
    if ( Array.isArray( value )) {
      processedValue = value.map( item => {
        if ( typeof item === 'string' && (( item.startsWith( '{' ) && !item.startsWith( '{{' )) || item.startsWith( '"' ))) {
          try {
            return JSON.parse( item );
          } catch ( e ) {
            return item;
          }
        }
        return item;
      });
    }

    setInputs( prev => ({ ...prev, [inputName]: processedValue }));
  };

  const handleCustomTagsKeyDown = useCallback(
    ( e: KeyboardEvent<HTMLInputElement> ) => {
      if ( e.key === 'Enter' || e.key === ',' ) {
        e.preventDefault();
        handleAddCustomTag();
      }
    },
    [ customTagsInput, customTags ]
  );

  const handleAddCustomTag = useCallback(
    () => {
      const trimmed = customTagsInput.trim();
      if ( trimmed && !customTags.includes( trimmed )) {
        setCustomTags( prev => [ ...prev, trimmed ]);
      }
      setCustomTagsInput( '' );
    },
    [ customTagsInput, customTags ]
  );

  const handleRemoveCustomTag = useCallback(
    ( index: number ) => {
      setCustomTags( prev => prev.filter(( _, i ) => i !== index ));
    },
    []
  );

  // Override-group identity is case- and whitespace-insensitive (matches
  // server walk in rules-utils.ts). Without this, "Stock Update" and
  // "stock-update" would silently coexist on the same rule and never override.
  const isDuplicateOverrideGroup = useCallback(
    ( candidate: string, existing: string[]): boolean => {
      const normalized = normalizeOverrideGroup( candidate );
      return existing.some(( g ) => normalizeOverrideGroup( g ) === normalized );
    },
    []
  );

  const handleAddOverrideGroup = useCallback(
    () => {
      const trimmed = overrideGroupsInput.trim();
      if ( trimmed && !isDuplicateOverrideGroup( trimmed, overrideGroups )) {
        setOverrideGroups( prev => [ ...prev, trimmed ]);
      }
      setOverrideGroupsInput( '' );
      setShowOverrideGroupsDropdown( false );
    },
    [ overrideGroupsInput, overrideGroups, isDuplicateOverrideGroup ]
  );

  const handleRemoveOverrideGroup = useCallback(
    ( index: number ) => {
      setOverrideGroups( prev => prev.filter(( _, i ) => i !== index ));
    },
    []
  );

  const handleSelectOverrideGroupOption = useCallback(
    ( option: string ) => {
      if ( !isDuplicateOverrideGroup( option, overrideGroups )) {
        setOverrideGroups( prev => [ ...prev, option ]);
        setOverrideGroupsInput( '' );
        setShowOverrideGroupsDropdown( false );
      }
    },
    [ overrideGroups, setShowOverrideGroupsDropdown, isDuplicateOverrideGroup ]
  );

  const getSelectOptions = ( declaredInput: any ) => {
    if ( declaredInput.materializedOptions ) {
      const groupedOptions: { group: string; items: { value: any; label: string }[] }[] = [];

      Object.entries( declaredInput.materializedOptions ).forEach(([ groupName, options ]) => {
        const groupItems = ( options as any[]).map( option => {
          // A null/undefined label crashes Mantine's defaultOptionsFilter
          // (option.label.toLowerCase()). Never pass a non-string label: fall
          // back to a readable id from the value, then to the raw value.
          const fallback = option.value && typeof option.value === 'object'
            ? ( option.value.connectorId ?? option.value.id ?? JSON.stringify( option.value ))
            : String( option.value ?? '' );
          return {
            // Preserve the original value structure (string or object)
            value: JSON.stringify( option.value ),
            label: option.label != null && option.label !== '' ? String( option.label ) : fallback
          };
        });

        groupedOptions.push({
          group: groupName,
          items: groupItems
        });
      });

      return groupedOptions;
    }
    return [];
  };

  const inputRenderers = {
    boolean: ( declaredInput: any, value: any ) => (
      <Switch
        key={declaredInput.inputName}
        label={declaredInput.label}
        description={declaredInput.description}
        checked={value || false}
        onChange={( event ) => handleInputChange( declaredInput.inputName, event.currentTarget.checked )}
      />
    ),

    number: ( declaredInput: any, value: any ) => (
      <NumberInput
        key={declaredInput.inputName}
        label={declaredInput.label}
        description={declaredInput.description}
        value={value || 0}
        onChange={( val ) => handleInputChange( declaredInput.inputName, val )}
        radius="xs"
        required={declaredInput.isRequired}
      />
    ),

    multiNumber: ( declaredInput: any, value: any ) => (
      <TextInput
        key={declaredInput.inputName}
        label={`${declaredInput.description} (${t( 'multi_number_description', 'Números separados por comas' )})`}
        value={Array.isArray( value ) ? value.join( ', ' ) : value || ''}
        onChange={( event ) => {
          const stringValue = event.currentTarget.value;
          const numbers = stringValue.split( ',' ).map( s => {
            const num = parseFloat( s.trim());
            return isNaN( num ) ? 0 : num;
          })
.filter( n => !isNaN( n ));
          handleInputChange( declaredInput.inputName, numbers );
        }}
        radius="xs"
        required={declaredInput.isRequired}
        placeholder={t( 'multi_number_placeholder', '1, 2, 3, 4' )}
      />
    ),

    string: ( declaredInput: any, value: any ) => (
      <TextInput
        key={declaredInput.inputName}
        label={declaredInput.label}
        description={declaredInput.description}
        value={value || ''}
        onChange={( event ) => handleInputChange( declaredInput.inputName, event.currentTarget.value )}
        radius="xs"
        required={declaredInput.isRequired}
      />
    ),

    multiString: ( declaredInput: any, value: any ) => (
      <TextInput
        key={declaredInput.inputName}
        label={declaredInput.label}
        description={`${declaredInput.description} (${t( 'multi_string_description', 'Textos separados por comas' )})`}
        value={Array.isArray( value ) ? value.join( ', ' ) : value || ''}
        onChange={( event ) => {
          const stringValue = event.currentTarget.value;
          const strings = stringValue.split( ',' ).map( s => s.trim())
.filter( s => s.length > 0 );
          handleInputChange( declaredInput.inputName, strings );
        }}
        radius="xs"
        required={declaredInput.isRequired}
        placeholder={t( 'multi_string_placeholder', 'opción1, opción2, opción3' )}
      />
    ),
    select: ( declaredInput: any, value: any ) => {
      // Convert complex object value to string for comparison with select options
      let stringifiedValue = '';
      if ( value ) {
        stringifiedValue = typeof value === 'string' ? value : JSON.stringify( value );
      }

      return (
        <Select
          key={declaredInput.inputName}
          label={declaredInput.label}
          description={declaredInput.description}
          value={stringifiedValue}
          onChange={( val ) => handleInputChange( declaredInput.inputName, val )}
          data={getSelectOptions( declaredInput )}
          radius="xs"
          required={declaredInput.isRequired}
          searchable
          checkIconPosition="right"
        />
      );
    },
    multiSelect: ( declaredInput: any, value: any ) => {
      // Convert complex object values to strings for comparison with select options
      let stringifiedValues: string[] = [];
      if ( Array.isArray( value )) {
        stringifiedValues = value.map( v => typeof v === 'string' ? v : JSON.stringify( v ));
      }

      return (
        <MultiSelect
          key={declaredInput.inputName}
          label={declaredInput.label}
          description={declaredInput.description}
          value={stringifiedValues}
          onChange={( val ) => handleInputChange( declaredInput.inputName, val )}
          data={getSelectOptions( declaredInput )}
          radius="xs"
          required={declaredInput.isRequired}
          searchable
          clearable
          checkIconPosition="right"
        />
      );
    },
    formula: ( declaredInput: any, value: any ) => {
      /**
       * `currentKitItemIds` is derived from the formula's materialized
       * variable chips themselves: the engine populates per-kit-item
       * chips ( `quantitiesByItem`, `priceListsByItem`,
       * `priceListsByQuantity` ) for exactly the items currently in the
       * publication's kit. Any itemId referenced by the saved formula
       * that does NOT appear in those chips is by definition no longer
       * in the kit — that's the missing-item warning condition.
       *
       * This avoids piping the publication's kit through a separate
       * channel ( the form does not have direct access to the
       * publication object — only `hierarchyEntityId` ) and keeps the
       * source of truth aligned with what the user can actually pick.
       */
      const flatVariables = flattenVariablesRecord(
        ( declaredInput.materializedOptions || {}) as Record<string, readonly MaterializedVariable[]>
      );
      const currentKitItemIds = new Set<string>();
      const missingItemLabels = new Map<string, string>();
      for ( const variable of flatVariables ) {
        const match = /itemId:'([^']+)'/.exec( variable.value );
        if ( match ) {
          currentKitItemIds.add( match[ 1 ]);
          if ( !missingItemLabels.has( match[ 1 ])) {
            missingItemLabels.set( match[ 1 ], variable.label );
          }
        }
      }
      const missingRefs = missingItemRefs( formulaItemRefs( value || '' ), currentKitItemIds );

      return (
        <>
          {missingRefs.size > 0 && (
            <Alert
              data-testid={componentsTestIds.ruleEditorFormulaWarningBanner}
              variant="light"
              color="yellow"
              radius="xs"
              icon={<IconAlertTriangle size={18} />}
              title={tAutomation(
                'automation_rule_editor_formula_missing_items_banner_title',
                'This formula references items no longer in the kit:'
              )}
              styles={{
                root: {
                  borderColor: 'light-dark(var(--mantine-color-yellow-3), var(--mantine-color-yellow-9))'
                },
                title: {
                  color: 'light-dark(var(--mantine-color-yellow-7), var(--mantine-color-yellow-3))'
                },
                icon: {
                  color: 'light-dark(var(--mantine-color-yellow-7), var(--mantine-color-yellow-3))'
                }
              }}
            >
              <List size="sm" spacing={2}>
                {Array.from( missingRefs ).map(( itemId ) => (
                  <List.Item key={itemId}>
                    {missingItemLabels.get( itemId ) ?? itemId}
                  </List.Item>
                ))}
              </List>
            </Alert>
          )}

          <FormulaInput
            key={declaredInput.inputName}
            label={declaredInput.label}
            description={declaredInput.description}
            value={value || ''}
            onChange={( val ) => handleInputChange( declaredInput.inputName, val )}
            variables={declaredInput.materializedOptions || {}}
            required={declaredInput.isRequired}
            placeholder={t( 'formula_input_placeholder', 'Ingresar fórmula (Ctrl+Espacio para sugerencias)' )}
            onValidate={( error ) => {setFormulaValidationError( error ); return error;}}
            currentKitItemIds={currentKitItemIds}
          />

          {/* Entity Selector - conditionally shown based on provider */}
        {previewEntityProvider && (
          <EntitySelector
            provider={previewEntityProvider}
            selectedOption={selectedEntityOption}
            onSelect={setSelectedEntityOption}
            ruleCategories={instantiatedRule?.categories}
          />
        )}

        <Stack gap="sm">
          {/* Preview Button */}
          <Tooltip
            label={
              (() => {
                if ( formulaValidationError ) {
                  return t( 'preview_disabled_validation_error', 'Fix validation errors first' );
                }
                if ( !value || value.trim() === '' ) {
                  return t( 'preview_disabled_empty', 'Enter a formula first' );
                }
                // Check if selector is shown and no entity selected
                const selectorShown = previewEntityProvider && ( !previewEntityProvider.shouldShow || previewEntityProvider.shouldShow( instantiatedRule?.categories || []));
                if ( selectorShown && !selectedEntityOption ) {
                  return t( 'preview_disabled_no_entity', 'Select an entity to preview this rule' );
                }
                return t( 'preview_formula_tooltip', 'Calculate the result of this formula with current values' );
              })()
            }
            position="top"
          >
            <Button
              onClick={handlePreviewFormulaValue}
              loading={previewFormulaValueLoading}
              disabled={
                !!formulaValidationError ||
                !value ||
                value.trim() === '' ||
                ( previewEntityProvider && ( !previewEntityProvider.shouldShow || previewEntityProvider.shouldShow( instantiatedRule?.categories || [])) && !selectedEntityOption )
              }
              variant="light"
              color="primary"
              size="xs"
              fullWidth
              leftSection={<IconEye size={14} />}
            >
              {t( 'preview_formula_button', 'Previsualizar fórmula' )}
            </Button>
          </Tooltip>

          {/* Result Display */}
          {previewFormulaValue && (
            <Paper
              p="md"
              radius="xs"
              withBorder
              style={{
                backgroundColor: previewFormulaValue.success
                  ? 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-5))'
                  : 'light-dark(var(--mantine-color-red-0), rgba(244, 67, 54, 0.15))',
                borderColor: previewFormulaValue.success
                  ? 'light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))'
                  : 'light-dark(var(--mantine-color-red-3), rgba(244, 67, 54, 0.3))',
                position: 'relative'
              }}
            >
              {previewFormulaValue.success ? (
                <Stack gap={4} align="center">
                  <Text size="xs" c="dimmed">
                    {t( 'preview_result_value_label', 'Resultado' )}
                  </Text>
                  {typeof previewFormulaValue.value === 'number' ? (
                    <Text size="xl" fw={600}>
                      {previewFormulaValue.value.toLocaleString( i18n.resolvedLanguage || 'es' )}
                    </Text>
                  ) : typeof previewFormulaValue.value === 'object' && previewFormulaValue.value !== null ? (
                    (() => {
                      const entries = Object.entries( previewFormulaValue.value );
                      if ( entries.length === 1 ) {
                        const val = entries[0][1];
                        return (
                          <Text size="xl" fw={600}>
                            {typeof val === 'number' ? val.toLocaleString( i18n.resolvedLanguage || 'es' ) : String( val )}
                          </Text>
                        );
                      }
                      return (
                        <Stack gap={2}>
                          {entries.map(([ , val ], index ) => (
                            <Group key={index} gap="xs" justify="space-between">
                              <Text size="sm" c="dimmed">{t( 'preview_variation_label', 'Variación' )} {index + 1}:</Text>
                              <Text size="sm" fw={600}>
                                {typeof val === 'number' ? val.toLocaleString( i18n.resolvedLanguage || 'es' ) : String( val )}
                              </Text>
                            </Group>
                          ))}
                        </Stack>
                      );
                    })()
                  ) : (
                    <Text size="xl" fw={600}>
                      {JSON.stringify( previewFormulaValue.value )}
                    </Text>
                  )}
                </Stack>
              ) : (
                <>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() => setPreviewFormulaValue( null )}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px'
                    }}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                  <Stack gap="xs" align="center">
                    <Group gap="xs">
                      <IconAlertCircle size={18} color="var(--mantine-color-red-7)" />
                      <Text size="sm" fw={500} c="red.7">
                        {t( 'preview_result_error_title', 'No se pudo calcular' )}
                      </Text>
                    </Group>
                    <Text size="sm" c="red.7" ta="center">
                      {previewFormulaValue.error || t( 'preview_result_error_generic', 'Error al calcular la vista previa' )}
                    </Text>
                  </Stack>
                </>
              )}
            </Paper>
          )}

          {/* Debug Button */}
          {( dryRunTrace || dryRunError ) && (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconBug size={14} />}
              onClick={() => setShowDebugModal( true )}
            >
              {t( 'preview_debug_button', 'Depurar ejecución' )}
            </Button>
          )}

          {/* Empty State */}
          {!previewFormulaValue && !previewFormulaValueLoading && (
            <Text size="xs" c="dimmed" ta="center">
              {t( 'preview_result_no_data', 'Haz clic en "Previsualizar fórmula" para ver el resultado' )}
            </Text>
          )}
        </Stack>
        </>
      );
    },
    template: ( declaredInput: any, value: any ) => (
      <TemplateInput
        key={declaredInput.inputName}
        label={declaredInput.label}
        description={declaredInput.description}
        value={value || ''}
        onChange={( val ) => handleInputChange( declaredInput.inputName, val )}
        variables={declaredInput.materializedOptions || {}}
        required={declaredInput.isRequired}
        placeholder={t( 'template_input_placeholder', 'Empieza a escribir (Ctrl+Espacio para sugerencias)' )}
      />
    )
  };

  const renderInput = ( declaredInput: any ) => {
    const value = inputs[declaredInput.inputName];
    const renderer = inputRenderers[declaredInput.type as keyof typeof inputRenderers];
    if ( renderer ) {
      return renderer( declaredInput, value );
    }

    // Default fallback
    return inputRenderers.string( declaredInput, value );
  };

  const handleSave = () => {
    const rule: SetOptional<RulesReadmodelType, 'hierarchyEntityIds'> = {
      id: instantiatedRule.id,
      ruleId: instantiatedRule.ruleId,
      accountId: instantiatedRule.accountId,
      hierarchyEntityIds: instantiatedRule.hierarchyEntityIds,
      categories: instantiatedRule.categories,
      hierarchy: ruleDefinition?.hierarchy || instantiatedRule.hierarchy,
      name: instantiatedRule.name,
      description: instantiatedRule.description,
      tags: instantiatedRule.tags,
      triggeringEvents: triggeringEvents,
      ruleDefinition: instantiatedRule.ruleDefinition,
      ruleDefinitionId: instantiatedRule.ruleDefinitionId,
      enabled,
      hierarchyLevel: instantiatedRule.hierarchyLevel,
      declaredInputsValues: inputs,
      declaredInputs: instantiatedRule.declaredInputs,
      customTags,
      ...( storedOverrideGroupsMissing.current && overrideGroups.length === 0 ? {} : { overrideGroups })
    };

    onSave( rule );
  };

  const isValid = () => {
    if ( !instantiatedRule.declaredInputs ) return true;
    return instantiatedRule.declaredInputs.every( declaredInput => {
      if ( !declaredInput.isRequired ) return true;
      const value = inputs[declaredInput.inputName || ''];
      return value !== undefined && value !== null && value !== '';
    });
  };

  return (
    <Card withBorder radius="xs" p="md">
      <RuleNodesViewModal
        opened={showNodesEditor}
        onClose={() => setShowNodesEditor( false )}
        dsl={ruleDefinition}
      />
      {showDebugModal && (
        <RuleNodesViewModal
          opened={showDebugModal}
          onClose={() => setShowDebugModal( false )}
          dsl={ruleDefinition}
          executionTrace={dryRunTrace ?? undefined}
          dryRunError={dryRunError ?? undefined}
        />
      )}
      <Stack gap="md">
        <Stack gap="xs">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="md" fw={500}>
              {instantiatedRule.name}
            </Text>
            <Group gap="xs">
              <Button
                variant="light"
                size="xs"
                color="primary"
                onClick={() => {
                  setShowNodesEditor( true );
                }}
              >
                {t( 'view_nodes_editor_button', 'Ver en editor de nodos' )}
              </Button>
              <Tooltip label={<Text size="xs">{t( 'rule_enabled_tooltip', 'Habilita o deshabilita esta regla' )}</Text>} position="left">
                <div>
                  <Switch
                    checked={enabled}
                    onChange={( event ) => setEnabled( event.currentTarget.checked )}
                    size="sm"
                  />
                </div>
              </Tooltip>
            </Group>
          </Group>
          <Text size="sm" c="dimmed">
            {instantiatedRule.description}
          </Text>
        </Stack>

        {( instantiatedRule as any ).possibleTriggeringEvents && ( instantiatedRule as any ).possibleTriggeringEvents.length > 0 && (
          <Stack gap="sm">
            <Stack gap="2px">
              <Text size="sm" fw={500}>
                {t( 'triggering_events_title', 'Eventos de activación' )}
              </Text>
              <Text size="xs" fw={400} c="dimmed">
                {t( 'rule_configuration_description', 'Selecciona uno o más eventos que activarán esta regla. Si no se selecciona ningún evento, la regla no se ejecutará automáticamente.' )}
              </Text>
            </Stack>
            <Group gap="md">
              {( instantiatedRule as any ).possibleTriggeringEvents.map(( event, index ) => (
                <Switch
                  key={index}
                  label={`${t( 'triggerring_event_friendly_name_prefix', 'Ejecutar si' )} ${friendlyName.Events?.[i18n.resolvedLanguage || 'es']?.[event]?.toLowerCase() || event}`}
                  checked={triggeringEvents.includes( event )}
                  onChange={( e ) => {
                    if ( e.currentTarget.checked ) {
                      setTriggeringEvents( prev => [ ...prev, event ]);
                    } else {
                      setTriggeringEvents( prev => prev.filter( ev => ev !== event ));
                    }
                  }}
                  size="sm"
                />
              ))}
            </Group>
          </Stack>
        )}

        {instantiatedRule.declaredInputs && instantiatedRule.declaredInputs.length > 0 && (
          <Stack gap="sm">
            <Text size="sm" fw={500}>
              {t( 'rule_configuration_title', 'Configuración' )}
            </Text>
            {instantiatedRule.declaredInputs.map( renderInput )}
          </Stack>
        )}
        {
          instantiatedRule.categories && instantiatedRule.categories.length > 0 && (
            <Stack gap="sm">
              <Text size="sm" fw={500}>
                {t( 'rule_categories_title', 'Categorías' )}
              </Text>
              <Group gap="sm">
                {instantiatedRule.categories.map(( category, index ) => (
                  <Badge key={index} variant="light" color="primary" size="sm">
                    {t( `rule_category_${category}`, category ) || category}
                  </Badge>
                ))}
              </Group>
            </Stack>
          )
        }
        <Stack gap="sm">
          <Stack gap="xs">
            <Popover
              opened={showOverrideGroupsDropdown && overrideGroupsOptions.length > 0}
              onClose={() => setShowOverrideGroupsDropdown( false )}
              width="target"
              position="bottom"
              positionDependencies={[]}
              shadow="md"
              withinPortal={false}
              closeOnClickOutside={true}
              clickOutsideEvents={[ 'mousedown', 'touchstart' ]}
            >
              <Popover.Target>
                <TextInput
                  description={t( 'rule_override_groups_description', 'Los grupos de sobreescritura permiten agrupar reglas aplicadas en diferentes entidades, para que las reglas en el mismo grupo puedan sobrescribirse entre sí.' )}
                  label={t( 'rule_override_groups_label', 'Agregar grupos de sobreescritura (presiona Enter o coma para agregar)' )}
                  value={overrideGroupsInput}
                  onChange={( event ) => setOverrideGroupsInput( event.currentTarget.value )}
                  onBlur={() => setShowOverrideGroupsDropdown( false )}
                  onKeyDown={( e ) => {
                    if ( e.key === 'Enter' || e.key === ',' ) {
                      e.preventDefault();
                      handleAddOverrideGroup();
                      setShowOverrideGroupsDropdown( false );
                    } else if ( e.ctrlKey && e.key === ' ' ) {
                      e.preventDefault();
                      setShowOverrideGroupsDropdown( prev => !prev );
                    }
                  }}
                  radius="xs"
                  placeholder={t( 'rule_override_groups_placeholder', 'Escribe un grupo o presiona Ctrl+Espacio para ver opciones' )}
                  rightSection={
                    <Group gap="xs">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="primary"
                        onClick={handleAddOverrideGroup}
                        disabled={!overrideGroupsInput.trim() || isDuplicateOverrideGroup( overrideGroupsInput, overrideGroups )}
                      >
                        <IconPlus size={16} />
                      </ActionIcon>
                    </Group>
                  }
                />
              </Popover.Target>
              <Popover.Dropdown p={4} style={{ maxHeight: 'calc(4 * (0.875rem + 8px + 8px))', overflowY: 'auto' }}>
                <Stack gap={4}>
                  {overrideGroupsOptions.filter( option =>
                    !isDuplicateOverrideGroup( option.id, overrideGroups ) &&
                    ( option.label.toLowerCase().includes( overrideGroupsInput.toLowerCase()) ||
                      option.id.toLowerCase().includes( overrideGroupsInput.toLowerCase()))).slice( 0, 10 )
.map( option => (
                    <UnstyledButton
                      key={option.id}
                      className={classes.dropdownItem}
                      onClick={() => {
                        handleSelectOverrideGroupOption( option.id );
                        setShowOverrideGroupsDropdown( false );
                      }}
                    >
                      {option.label}
                    </UnstyledButton>
                  ))}
                </Stack>
              </Popover.Dropdown>
            </Popover>
            {overrideGroups.length > 0 && (
              <Group gap="xs">
                {overrideGroups.map(( group, index ) => (
                  <Badge
                    key={index}
                    variant="light"
                    color="primary"
                    radius="xs"
                    rightSection={
                      <ActionIcon
                        size="xs"
                        color="primary"
                        radius="xl"
                        variant="transparent"
                        onClick={() => handleRemoveOverrideGroup( index )}
                      >
                        <IconX size={10} />
                      </ActionIcon>
                    }
                  >
                    {group}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        </Stack>

        <Stack gap="sm">
          <Stack gap="xs">
            <TextInput
              description={t( 'rule_custom_tags_description', 'Las etiquetas personalizadas permiten categorizar y ordenar tus reglas para facilitar su gestión y búsqueda.' )}
              label={t( 'rule_custom_tags_label', 'Agregar etiquetas (presiona Enter o coma para agregar)' )}
              value={customTagsInput}
              onChange={( event ) => setCustomTagsInput( event.currentTarget.value )}
              onKeyDown={handleCustomTagsKeyDown}
              radius="xs"
              placeholder={t( 'rule_tags_placeholder', 'Escribe una etiqueta y presiona Enter o coma' )}
              rightSection={
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="primary"
                  onClick={handleAddCustomTag}
                  disabled={!customTagsInput.trim() || customTags.includes( customTagsInput.trim())}
                >
                  <IconPlus size={16} />
                </ActionIcon>
              }
            />
            {customTags.length > 0 && (
              <Group gap="xs">
                {customTags.map(( tag, index ) => (
                  <Badge
                    key={index}
                    variant="light"
                    color="gray"
                    radius="xs"
                    rightSection={
                      <ActionIcon
                        size="xs"
                        color="gray"
                        radius="xl"
                        variant="transparent"
                        onClick={() => handleRemoveCustomTag( index )}
                      >
                        <IconX size={10} />
                      </ActionIcon>
                    }
                  >
                    {tag}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        </Stack>


      </Stack>
    </Card>
  );
});

RuleConfigurationForm.displayName = 'RuleConfigurationForm';
